if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.ignoreNextEscapeKeyUp = false;
      this.photoSwipeEscapeAt = 0;
      this.suppressEscapeKeyUpOnce = false;
      this.handlePhotoSwipeEscapeKeyDown = this.handlePhotoSwipeEscapeKeyDown.bind(this);
      this.handlePhotoSwipeEscapeKeyUpCapture = this.handlePhotoSwipeEscapeKeyUpCapture.bind(this);
    }

    handlePhotoSwipeEscapeKeyDown(event) {
      if (!event.code || event.code.toUpperCase() !== 'ESCAPE') return;
      if (!this.hasAttribute('open')) return;

      const photoSwipeOpen = document.querySelector('.pswp.pswp--open');
      if (!photoSwipeOpen) return;

      this.ignoreNextEscapeKeyUp = true;
      this.photoSwipeEscapeAt = Date.now();
      this.suppressEscapeKeyUpOnce = true;
    }

    handlePhotoSwipeEscapeKeyUpCapture(event) {
      if (!event.code || event.code.toUpperCase() !== 'ESCAPE') return;
      if (!this.hasAttribute('open')) return;
      if (!this.suppressEscapeKeyUpOnce) return;

      this.suppressEscapeKeyUpOnce = false;
      this.ignoreNextEscapeKeyUp = false;
      this.photoSwipeEscapeAt = 0;
      this.escapeHandledByPhotoSwipe = false;

      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      if (typeof event.stopPropagation === 'function') event.stopPropagation();
      if (typeof event.preventDefault === 'function') event.preventDefault();
    }

    onKeyDown(event) {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') {
        const eventTarget = event.target;
        const targetInsidePhotoSwipe = eventTarget instanceof Element && eventTarget.closest('.pswp');
        const photoSwipeOpen = document.querySelector('.pswp.pswp--open');

        if (targetInsidePhotoSwipe || photoSwipeOpen) {
          this.ignoreNextEscapeKeyUp = true;
          this.photoSwipeEscapeAt = Date.now();
        }
      }

      super.onKeyDown(event);
    }

    onKeyUp(event) {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') {
        const eventTarget = event.target;
        const targetInsidePhotoSwipe = eventTarget instanceof Element && eventTarget.closest('.pswp');
        if (targetInsidePhotoSwipe) {
          this.ignoreNextEscapeKeyUp = false;
          this.photoSwipeEscapeAt = 0;
          this.escapeHandledByPhotoSwipe = false;
          return;
        }

        const justClosedPhotoSwipe = this.photoSwipeEscapeAt > 0 && Date.now() - this.photoSwipeEscapeAt < 1500;

        if (this.ignoreNextEscapeKeyUp || justClosedPhotoSwipe) {
          this.ignoreNextEscapeKeyUp = false;
          this.photoSwipeEscapeAt = 0;
          this.escapeHandledByPhotoSwipe = false;
          return;
        }
      }

      super.onKeyUp(event);
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';
      this.ignoreNextEscapeKeyUp = false;
      this.photoSwipeEscapeAt = 0;
      this.suppressEscapeKeyUpOnce = false;
      document.removeEventListener('keydown', this.handlePhotoSwipeEscapeKeyDown, true);
      document.removeEventListener('keyup', this.handlePhotoSwipeEscapeKeyUpCapture, true);

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.updateImageSizes();
          this.preventVariantURLSwitching();

          // Keep Escape in PhotoSwipe from also closing the quick-add modal on keyup.
          document.removeEventListener('keydown', this.handlePhotoSwipeEscapeKeyDown, true);
          document.removeEventListener('keyup', this.handlePhotoSwipeEscapeKeyUpCapture, true);
          document.addEventListener('keydown', this.handlePhotoSwipeEscapeKeyDown, true);
          document.addEventListener('keyup', this.handlePhotoSwipeEscapeKeyUpCapture, true);

          super.show(opener);

          // Quick add content is injected asynchronously; enforce initial focus once rendering settles.
          window.setTimeout(() => {
            if (!this.hasAttribute('open')) return;

            const dialog = this.querySelector('[role="dialog"]') || this;
            if (dialog.contains(document.activeElement)) return;

            const focusTarget = this.getFirstFocusableElement(dialog) || dialog;
            focusTarget.focus();

            if (!dialog.contains(document.activeElement)) {
              dialog.focus();
            }
          }, 120);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 768px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
